import React, { useState, useEffect } from 'react';
import { X, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export const InquiryDialog = ({ open, onClose, product, prefillMessage = '', titleOverride = null }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: prefillMessage,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Lock body scroll while open
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Reset state when product changes / dialog re-opens; re-apply prefill.
  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setError('');
      setForm((prev) => ({ ...prev, message: prefillMessage || '' }));
    }
  }, [open, product?.id, prefillMessage]);

  if (!open || !product) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in your name, email, and a short message.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${backendUrl}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: product.id,
          productTitle: product.title,
          productSlug: product.slug,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          message: form.message.trim(),
        }),
      });
      if (!res.ok) throw new Error('Submit failed');
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError('Something went wrong sending your inquiry. Please email joseph@josephgravesjr.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6" data-testid="inquiry-dialog">
      <div
        className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-lg bg-cream text-ink shadow-elevated max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-soft hover:text-ink transition-colors"
          aria-label="Close inquiry"
          data-testid="inquiry-close"
        >
          <X className="w-5 h-5" />
        </button>

        {submitted ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-terracotta/10 flex items-center justify-center">
              <Check className="w-8 h-8 text-terracotta" />
            </div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-3">Inquiry sent</p>
            <h3 className="font-display text-3xl mb-4 tracking-tight">Thank you.</h3>
            <p className="text-soft leading-relaxed">
              Joseph will personally respond within 1–2 business days regarding{' '}
              <span className="italic">{product.title}</span>.
            </p>
            <button
              onClick={onClose}
              className="mt-8 inline-flex items-center justify-center px-8 py-4 text-xs uppercase tracking-[0.18em] bg-ink text-cream hover:bg-terracotta transition-colors"
              data-testid="inquiry-done-btn"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-8 sm:p-10">
            <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-3">
              {titleOverride
                ? 'Inquire — Custom Request'
                : `Inquire — ${product.collection === 'originals' ? 'Original Painting' : 'Limited Piece'}`}
            </p>
            <h3 className="font-display text-3xl sm:text-4xl mb-2 tracking-tight">{titleOverride || product.title}</h3>
            <p className="text-sm text-soft mb-8 leading-relaxed">
              Send a note to the studio. Joseph reads every inquiry personally and will share pricing,
              availability, and provenance details directly with you.
            </p>

            <div className="space-y-5">
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-soft block mb-2" htmlFor="inq-name">
                  Name
                </label>
                <input
                  id="inq-name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  type="text"
                  className="w-full bg-transparent border-b border-ink/20 focus:border-ink py-2 outline-none text-ink"
                  data-testid="inquiry-name"
                  required
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-soft block mb-2" htmlFor="inq-email">
                  Email
                </label>
                <input
                  id="inq-email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  type="email"
                  className="w-full bg-transparent border-b border-ink/20 focus:border-ink py-2 outline-none text-ink"
                  data-testid="inquiry-email"
                  required
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-soft block mb-2" htmlFor="inq-phone">
                  Phone (optional)
                </label>
                <input
                  id="inq-phone"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  type="tel"
                  className="w-full bg-transparent border-b border-ink/20 focus:border-ink py-2 outline-none text-ink"
                  data-testid="inquiry-phone"
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-soft block mb-2" htmlFor="inq-message">
                  Message
                </label>
                <textarea
                  id="inq-message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Tell us a bit about your interest — for your home, a gift, a collection..."
                  className="w-full bg-transparent border border-ink/20 focus:border-ink p-3 outline-none text-ink resize-none"
                  data-testid="inquiry-message"
                  required
                />
              </div>
            </div>

            {error && (
              <p className="mt-5 text-sm text-terracotta" data-testid="inquiry-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className={cn(
                'mt-8 w-full inline-flex items-center justify-center gap-3 px-8 py-4',
                'text-xs uppercase tracking-[0.18em] font-medium',
                'bg-ink text-cream hover:bg-terracotta transition-colors',
                submitting && 'opacity-60 cursor-not-allowed'
              )}
              data-testid="inquiry-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Inquiry'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default InquiryDialog;
